import { Alert, Button, Card, Col, Empty, Row, Space, Statistic, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type Statement = {
  customer: { id: string; customer_code?: string; customer_name?: string };
  students: Array<{ student_id: string; admission_no?: string; first_name?: string; last_name?: string }>;
  demands: Array<{
    id: string;
    admission_no?: string;
    period_key?: string;
    fee_head_code?: string;
    amount: number;
    status: string;
    collection_hold?: number | boolean;
  }>;
  promises: Array<{ id: string; promised_amount: number; promised_on: string; status: string }>;
  disputes: Array<{ id: string; reason: string; status: string }>;
  summary: {
    student_count: number;
    demand_count: number;
    outstanding: number;
    open_promise_count: number;
    open_dispute_count: number;
  };
  links: { finance_customer_statement: string };
};

export default function FeeFamilyStatementPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const studentId = params.get('student_id') || undefined;

  const query = useQuery({
    queryKey: ['education-fee-family-statement', studentId],
    enabled: Boolean(studentId),
    queryFn: () =>
      api.get<Statement>('/education/fees/family-statement', { params: { student_id: studentId } }),
  });
  const data = query.data?.data;

  return (
    <EducationPageChrome
      title={t('feeFamilyStatement')}
      description={t('feeFamilyStatementIntro')}
      extra={<Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <EducationStudentSelect
            allowClear
            style={{ width: 360, maxWidth: '100%' }}
            value={studentId}
            onChange={(value) => {
              const next = new URLSearchParams(params);
              if (value) next.set('student_id', value);
              else next.delete('student_id');
              setParams(next);
            }}
          />
        </Card>
        {!studentId ? (
          <Empty description={t('selectStudentForFamilyStatement', { defaultValue: 'Select a student to load the family payer statement' })} />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              message={t('feeFamilyStatementHint', {
                defaultValue:
                  'Shows Education demands for all students linked to the same primary payer customer. AR detail stays in Finance — this is not a second statement of account.',
              })}
            />
            <Card size="small" loading={query.isLoading}>
              {data ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Tag>{data.customer.customer_code || data.customer.id}</Tag>
                    <span>{data.customer.customer_name}</span>
                  </div>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} md={6}>
                      <Statistic title={t('students')} value={data.summary.student_count} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic title={t('feeDemands')} value={data.summary.demand_count} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic title={t('outstanding')} value={data.summary.outstanding} />
                    </Col>
                    <Col xs={12} md={6}>
                      <Statistic
                        title={t('openPromisesDisputes', { defaultValue: 'Open promises / disputes' })}
                        value={`${data.summary.open_promise_count} / ${data.summary.open_dispute_count}`}
                      />
                    </Col>
                  </Row>
                  <Button onClick={() => navigate(data.links.finance_customer_statement)}>
                    {t('openFinanceCustomerStatement', { defaultValue: 'Open Finance customer statement' })}
                  </Button>
                </Space>
              ) : null}
            </Card>
            <Card size="small" title={t('students')}>
              <Table
                rowKey="student_id"
                size="small"
                loading={query.isLoading}
                dataSource={data?.students || []}
                pagination={false}
                columns={[
                  { title: t('admissionNo'), dataIndex: 'admission_no' },
                  {
                    title: t('student'),
                    render: (_: unknown, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
                  },
                ]}
              />
            </Card>
            <Card size="small" title={t('feeDemands')}>
              <Table
                rowKey="id"
                size="small"
                loading={query.isLoading}
                dataSource={data?.demands || []}
                pagination={{ pageSize: 20 }}
                columns={[
                  { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
                  { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
                  { title: t('feeHead'), dataIndex: 'fee_head_code', width: 120 },
                  { title: t('amount'), dataIndex: 'amount', width: 100 },
                  {
                    title: t('status'),
                    dataIndex: 'status',
                    width: 120,
                    render: (v: string, row) => (
                      <Space size={4}>
                        <Tag>{v}</Tag>
                        {row.collection_hold ? <Tag color="orange">{t('collectionHold')}</Tag> : null}
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>
          </>
        )}
      </Space>
    </EducationPageChrome>
  );
}
